import { useCallback, useContext, useEffect, useReducer } from "react";
import { useParams } from "react-router";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { MODULE_ARC_PLC } from "components/Menu/config/constants";
import { useBlockerPrompt } from "components/Menu/hooks/useBlockerPrompt";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import { FARM_PAYMENT_CATEGORY_OTHER_GOVERNMENT } from "./FarmPaymentConstants";

const FARM_PAYMENT_PLACEHOLDER = {
  location_state_name: "",
  location_county_name: "",
  insurance_plan_name: "Payment",
  type_name: "",
  practice_name: "",
};

export const useFarmPaymentForm = () => {
  const { farm_payment_id } = useParams();
  const { globalState } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const { component: formStatusComponent, actions: formStatusActions, state: formStatusState } = useFormStatus();

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const [state, dispatch] = useReducer(propUpdateReducer, {
    is_active: true,
    module: MODULE_ARC_PLC,
    payment_category: FARM_PAYMENT_CATEGORY_OTHER_GOVERNMENT,
    payment_amount: 0,
    producer_token: globalState?.producer_token,
    ...FARM_PAYMENT_PLACEHOLDER,
  });

  useEffect(() => {
    const id = String(farm_payment_id) === "0" ? uuidv4() : farm_payment_id;
    dispatch({
      type: "update",
      payload: { key: "id", value: id },
    });
  }, [farm_payment_id]);

  useEffect(() => {
    dispatch({
      type: "updateMany",
      payload: {
        reinsurance_year: globalState?.crop_year,
        module: MODULE_ARC_PLC,
        producer_token: globalState?.producer_token,
      },
    });
  }, [globalState?.crop_year, globalState?.producer_token]);

  const loadFarmPayment = useCallback(async () => {
    if (!globalState?.producer_token || !state?.id || String(state.id) === "0") {
      return;
    }

    try {
      const producerFilter = {
        producer_token: globalState.producer_token,
        id: state.id,
        module: MODULE_ARC_PLC,
        is_active: true,
      };

      const payments = await UsersApiHelper.users_search("InsurancePlanClaim", producerFilter, globalState);

      if (payments?.length) {
        dispatch({
          type: "updateMany",
          payload: payments[0],
        });
      }
    } catch (err) {
      handleError(err);
    }
  }, [globalState, state?.id]);

  useEffect(() => {
    if (state?.id && String(state.id) !== "0") {
      loadFarmPayment();
    }
  }, [state?.id, globalState?.producer_token, loadFarmPayment]);

  const handleChangeStateSelect = (event) => {
    formStatusActions.setIsFormDirty(true, false);
    dispatch({
      type: "update",
      payload: { key: event.target.name, value: event.target.value },
    });
  };

  const handleChangeState = (event, value) => {
    if (!event?.target) return;

    const nextValue = value ?? event.target.value;
    const fieldName = event.target.name;

    if (state[fieldName] !== nextValue) {
      formStatusActions.setIsFormDirty(true, false);
      dispatch({
        type: "update",
        payload: { key: fieldName, value: nextValue },
      });
    }
  };

  const buildFarmPaymentPayload = useCallback(() => {
    return {
      ...state,
      ...FARM_PAYMENT_PLACEHOLDER,
      producer_token: state?.producer_token ?? globalState?.producer_token,
      module: MODULE_ARC_PLC,
      reinsurance_year: state?.reinsurance_year ?? globalState?.crop_year,
      commodity_name: state?.commodity_name?.trim?.() ? state.commodity_name.trim() : "",
      payment_amount: state?.payment_amount ?? 0,
      insurance_plan_id: null,
      policy_number: null,
    };
  }, [state, globalState?.crop_year, globalState?.producer_token]);

  const updateFarmPayment = useCallback(async () => {
    if (!globalState?.producer_token || !formStatusState?.isFormDirty) {
      return;
    }

    try {
      formStatusActions.setIsSaving(true);

      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null,
      });

      await api.callPost(
        "InsurancePlanClaim",
        {
          module: MODULE_ARC_PLC,
          InsurancePlanClaim: [buildFarmPaymentPayload()],
          producer_token: globalState.producer_token,
        },
        globalState
      );

      await loadFarmPayment();
      formStatusActions.setIsSaving(false);
    } catch (err) {
      formStatusActions.setIsFormDirty(true, false);
      formStatusActions.setIsSaving(false);
      handleError(err);
    }
  }, [buildFarmPaymentPayload, formStatusState?.isFormDirty, globalState, loadFarmPayment]);

  const isValid = Boolean(state?.id && String(state.id) !== "0");

  useEffect(() => {
    if (isValid && globalState?.producer_token && formStatusState?.isFormDirty) {
      updateFarmPayment();
    }
  }, [isValid, globalState?.producer_token, formStatusState?.isFormDirty, updateFarmPayment]);

  useBlockerPrompt(Boolean(formStatusState?.isFormDirty));

  return {
    state,
    formStatusComponent,
    formStatusState,
    handleChangeStateSelect,
    handleChangeState,
  };
};
